import { Button } from '@/components/ui/button';
import MinusButton from '@/components/ui/minus-button';
import PlusButton from '@/components/ui/plus-button';
import { Separator } from '@/components/ui/separator';
import { useCart } from '@/hooks/use-cart';
import { useDictionary } from '@/lib/context/locale-provider';
import CheckBoxOff from '@/public/images/ic_cbox_off.svg';
import CheckBoxOn from '@/public/images/ic_cbox_on.svg';
import {
  useDeleteCartItemOptionsMutation,
  useUpdateCartItemOptionMutation,
} from '@/slice/cartApi';
import { X } from 'lucide-react';
import Image from 'next/image';
import React from 'react';

function CartOptionItem({
  shopId,
  option,
  currency,
}: {
  shopId: string;
  option: {
    count: number;
    isValid: boolean;
    userItemName: string;
    userPrice: number;
    salesCounting: number;
    serial: string;
    krItemName: string;
    krwPrice: number;
    isSoldOut: boolean;
  };
  currency: string;
}) {
  const dict = useDictionary();
  const [updateCartItemOption] = useUpdateCartItemOptionMutation();
  const [deleteCartItemOptions] = useDeleteCartItemOptionsMutation();

  const { state, dispatch } = useCart();
  const isChecked = state.selectedItems.includes(option.serial);

  const [count, setCount] = React.useState(option.count);

  const handleChange = async () => {
    try {
      await updateCartItemOption({
        shopId,
        serial: option.serial,
        count,
      }).unwrap();
    } catch (e: any) {
      console.error('handleChange', e.message);
    }
  };

  const handleToggle = () => {
    dispatch({
      type: 'TOGGLE_ITEM',
      payload: option.serial,
    });
  };

  const handleSelectDelete = async () => {
    await deleteCartItemOptions([option.serial]);
  };

  React.useEffect(() => {
    setCount(option.count);
  }, [option]);

  return (
    <div className="inline-flex flex-col">
      <div className="inline-flex flex-row items-center justify-between">
        <div className="inline-flex flex-row items-start gap-2.5">
          {option.isSoldOut ? (
            <div className="w-5 h-5"></div>
          ) : (
            <Button
              variant="ghost"
              className="p-0 w-5 h-5 relative"
              onClick={handleToggle}
            >
              <Image
                src={isChecked ? CheckBoxOn : CheckBoxOff}
                alt={`Select ${option.userItemName}`}
                fill
              />
            </Button>
          )}
          <div className="inline-flex flex-col gap-[5px]">
            <span
              className={`${option.isSoldOut ? 'text-[#BBBBBB]' : 'giftifan5'} font-medium text-base`}
            >
              {option.userItemName}
            </span>
            <span
              className={`${option.isSoldOut ? 'text-[#BBBBBB]' : 'giftifan5'} font-bold text-xl`}
            >
              {Number(
                (option.userPrice * option.count).toFixed(2),
              ).toLocaleString('en-US')}
              {currency}
            </span>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="items-baseline"
          onClick={handleSelectDelete}
        >
          <X className="text-[#BBBBBB]" />
        </Button>
      </div>
      <div className="inline-flex flex-row-reverse gap-2.5">
        <div className="inline-flex flex-row-reverse">
          <PlusButton
            onClick={() => {
              setCount(prev => prev + 1);
            }}
          />
          <div className="min-w-8 h-6 text-center bg-[#F5F5F5] text-lg font-medium giftifan5 leading-6">
            {count}
          </div>
          <MinusButton
            onClick={() => {
              setCount(prev => prev - 1);
            }}
            disabled={count === 1}
          />
        </div>
        <Button
          variant="ghost"
          className="px-[5px] py-0.5 h-6 bg-[#FF7395] rounded-[8px] text-white disabled:bg-[#DDDDDD]"
          disabled={count === option.count}
          onClick={handleChange}
        >
          {dict!.active.update}
        </Button>
      </div>
      <Separator className="text-[#EEEEEE] mt-[15px]" />
    </div>
  );
}

export default CartOptionItem;
